import React, {
  useEffect,
  useState,
} from "react";

import {
  CircularProgress,
} from "@mui/material";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import toast from "react-hot-toast";

import AdvertisementForm from "../components/advertisements/AdvertisementForm";

import {
  advertisementsApi,
} from "../api/advertisementsApi";

import type {
  Advertisement,
  AdvertisementMediaChanges,
  CreateAdvertisementInput,
} from "../types/advertisement.types";

import {
  useAuth,
} from "../contexts/AuthContext";

import "../screenstyles/Advertisements.css";

const EditAdvertisement:
React.FC = () => {
  const {
    id,
  } =
    useParams<{
      id: string;
    }>();

  const navigate =
    useNavigate();

  const {
    hasPermission,
  } = useAuth();

  const [
    advertisement,
    setAdvertisement,
  ] =
    useState<Advertisement | null>(
      null,
    );

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    submitting,
    setSubmitting,
  ] =
    useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(
        false,
      );

      return;
    }

    let active =
      true;

    const load =
      async () => {
        try {
          const result =
            await advertisementsApi
              .getById(
                id,
              );

          if (active) {
            setAdvertisement(
              result,
            );
          }
        } catch (
          error
        ) {
          console.error(
            error,
          );

          toast.error(
            "تعذر تحميل الإعلان",
          );
        } finally {
          if (active) {
            setLoading(
              false,
            );
          }
        }
      };

    load();

    return () => {
      active =
        false;
    };
  }, [
    id,
  ]);

  if (
    !hasPermission(
      "advertisements.update",
    )
  ) {
    return (
      <div className="advertisement-access-denied">
        <div className="advertisement-access-denied-card">
          <h2 className="advertisement-access-denied-title">
            لا تملك صلاحية تعديل الإعلانات.
          </h2>
        </div>
      </div>
    );
  }

  if (
    loading
  ) {
    return (
      <div className="advertisement-page-loading">
        <CircularProgress />
      </div>
    );
  }

  if (
    !id ||
    !advertisement
  ) {
    return (
      <div className="advertisement-access-denied">
        <div className="advertisement-access-denied-card">
          <h2 className="advertisement-access-denied-title">
            الإعلان غير موجود.
          </h2>
        </div>
      </div>
    );
  }

  const handleSubmit =
    async (
      input:
        CreateAdvertisementInput,

      media:
        AdvertisementMediaChanges,
    ) => {
      try {
        setSubmitting(
          true,
        );

        /**
         * Update normal advertisement data first.
         */
        await advertisementsApi
          .update(
            id,
            input,
          );

        /**
         * Main image replacement.
         *
         * Backend itself removes old image after
         * successfully storing the replacement.
         */
        if (
          media.mainImageFile
        ) {
          await advertisementsApi
            .uploadMainImage(
              id,

              media.mainImageFile,
            );
        } else if (
          media.deleteMainImage
        ) {
          await advertisementsApi
            .deleteMainImage(
              id,
            );
        }

        /**
         * Mobile image.
         */
        if (
          media.mobileImageFile
        ) {
          await advertisementsApi
            .uploadMobileImage(
              id,

              media.mobileImageFile,
            );
        } else if (
          media.deleteMobileImage
        ) {
          await advertisementsApi
            .deleteMobileImage(
              id,
            );
        }

        toast.success(
          "تم تحديث الإعلان والصور بنجاح",
        );

        navigate(
          `/advertisements/${id}`,
        );
      } catch (
        error
      ) {
        console.error(
          error,
        );

        toast.error(
          "تعذر إكمال تحديث الإعلان",
        );
      } finally {
        setSubmitting(
          false,
        );
      }
    };

  return (
    <main
      className="
        advertisement-page
        advertisement-page-edit
      "
      dir="rtl"
    >
      <header className="advertisement-page-header">
        <div className="advertisement-page-header-content">
          <span className="advertisement-page-eyebrow">
            تعديل الإعلان
          </span>

          <h1 className="advertisement-page-title">
            {
              advertisement.name
            }
          </h1>

          <p className="advertisement-page-description">
            تعديل بيانات الإعلان
            والاستهداف والجدولة
            واستبدال الصور المخزنة
            في Firebase Storage.
          </p>

          <div className="advertisement-page-meta">
            <span className="advertisement-page-meta-chip">
              الحالة:{" "}
              {
                advertisement.status
              }
            </span>

            <span className="advertisement-page-meta-chip">
              الصورة الرئيسية:{" "}
              {advertisement
                .media
                .imageUrl
                ? "موجودة"
                : "غير موجودة"}
            </span>
          </div>
        </div>

        <div className="advertisement-page-header-actions">
          <button
            type="button"
            className="advertisement-page-back-btn"
            onClick={() =>
              navigate(
                `/advertisements/${id}`,
              )
            }
          >
            رجوع إلى التفاصيل
          </button>
        </div>
      </header>

      <AdvertisementForm
        initialAdvertisement={
          advertisement
        }
        submitLabel="حفظ التعديلات"
        submitting={
          submitting
        }
        onSubmit={
          handleSubmit
        }
        onCancel={() =>
          navigate(
            `/advertisements/${id}`,
          )
        }
      />
    </main>
  );
};

export default EditAdvertisement;